import { foldBundleEventBindings } from '@cogo/publisher-core/event-binding-shape'

export interface RuntimeContractSummary {
  eventCount: number
  eventTypes: string[]
  flowCount: number
  localStateCount: number
  appDataWriteTargets: string[]
  localDbWriteTargets: string[]
  externalDatasourceTargets: string[]
  resultNames: string[]
  computedNames: string[]
  stateScopes: string[]
  webMcpToolContracts: string[]
  domainActionContracts: string[]
  capabilityNotes: string[]
}

export interface RuntimeContractInput {
  manifest?: Record<string, unknown> | null
  blocks?: Record<string, unknown> | null
  flows?: Record<string, unknown> | null
  data?: Record<string, unknown> | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function sorted(values: Iterable<string>): string[] {
  return Array.from(values).filter(Boolean).sort((a, b) => a.localeCompare(b))
}

function addTargetScope(target: unknown, scopes: Set<string>, appDataTargets: Set<string>) {
  if (typeof target !== 'string') return
  const [scope] = target.split('.')
  if (scope) scopes.add(scope)
  if (target.startsWith('appData.')) appDataTargets.add(target)
}

function addStateScope(target: unknown, scopes: Set<string>) {
  if (typeof target !== 'string') return
  const [scope] = target.split('.')
  if (scope) scopes.add(scope)
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function sourceLocationLabel(source: unknown): string | null {
  if (!isRecord(source)) return null
  const file = stringValue(source.file)
  if (!file) return null
  const line = typeof source.line === 'number' ? `:${source.line}` : ''
  return `${file}${line}`
}

function addWebMcpDatasourceContract(name: string, source: unknown, contracts: Set<string>) {
  if (!isRecord(source)) return
  const kind = stringValue(source.kind) ?? stringValue(source.type)
  if (kind?.toLowerCase() !== 'webmcp') return
  const label =
    stringValue(source.call) ??
    stringValue(source.name) ??
    stringValue(source.importSource) ??
    name
  const sourceLabel = sourceLocationLabel(source.source) ?? stringValue(source.importSource)
  contracts.add(sourceLabel ? `${label} @ ${sourceLabel}` : label)
}

function collectDatasourceContracts(value: unknown, contracts: Set<string>) {
  if (!isRecord(value)) return
  for (const [name, source] of Object.entries(value)) {
    addWebMcpDatasourceContract(name, source, contracts)
  }
}

function isLocalDbOperation(operation: unknown): boolean {
  return (
    typeof operation === 'string' &&
    ['append', 'upsert', 'updateWhere', 'removeWhere'].includes(operation)
  )
}

function isExternalDatasourceAction(action: unknown): boolean {
  if (typeof action !== 'string') return false
  const normalized = action.toLowerCase()
  return (
    normalized.includes('rest') ||
    normalized.includes('graphql') ||
    normalized.includes('supabase') ||
    normalized.includes('websocket') ||
    normalized.includes('payment') ||
    normalized.includes('gemini')
  )
}

function walkSteps(
  steps: unknown,
  resultNames: Set<string>,
  stateScopes: Set<string>,
  appDataTargets: Set<string>,
  localDbTargets: Set<string>,
  externalDatasourceTargets: Set<string>,
  domainActionContracts: Set<string>,
) {
  if (!Array.isArray(steps)) return
  for (const step of steps) {
    if (!isRecord(step)) continue
    if (typeof step.name === 'string') resultNames.add(step.name)

    const params = isRecord(step.params) ? step.params : null
    if (isLocalDbOperation(params?.operation) && typeof params?.target === 'string') {
      addStateScope(params.target, stateScopes)
      localDbTargets.add(params.target)
    } else if (isExternalDatasourceAction(step.action)) {
      const resultTarget = params?.resultTarget ?? params?.target
      if (typeof resultTarget === 'string') {
        addStateScope(resultTarget, stateScopes)
        externalDatasourceTargets.add(resultTarget)
      }
    } else {
      addTargetScope(params?.target, stateScopes, appDataTargets)
    }

    const stepKind = (stringValue(step.type) ?? stringValue(step.action) ?? '').toLowerCase()
    if (stepKind === 'domain-operation') {
      const target = stringValue(step.target) ?? stringValue(params?.target) ?? stringValue(step.name) ?? 'domain-operation'
      const source = stringValue(step.sourceExpression) ?? stringValue(params?.sourceExpression)
      domainActionContracts.add(source ? `${target} -> ${source}` : target)
    }

    walkSteps(step.then, resultNames, stateScopes, appDataTargets, localDbTargets, externalDatasourceTargets, domainActionContracts)
    walkSteps(step.else, resultNames, stateScopes, appDataTargets, localDbTargets, externalDatasourceTargets, domainActionContracts)
    walkSteps(step.steps, resultNames, stateScopes, appDataTargets, localDbTargets, externalDatasourceTargets, domainActionContracts)
    if (Array.isArray(step.branches)) {
      for (const branch of step.branches) {
        if (isRecord(branch)) {
          walkSteps(branch.steps, resultNames, stateScopes, appDataTargets, localDbTargets, externalDatasourceTargets, domainActionContracts)
          walkSteps(branch.then, resultNames, stateScopes, appDataTargets, localDbTargets, externalDatasourceTargets, domainActionContracts)
        } else {
          walkSteps(branch, resultNames, stateScopes, appDataTargets, localDbTargets, externalDatasourceTargets, domainActionContracts)
        }
      }
    }
  }
}

function collectEventTypes(events: unknown, eventTypes: Set<string>): number {
  if (!isRecord(events)) return 0
  let count = 0
  // Task 4 / authority-repair (2026-08-09, round 2): ONE fold. The previous
  // `bindings` branch plus top-level loop skipped only the literal key
  // 'bindings', so an `elements`-shaped artifact — what the agentic generator
  // emits 357/357 times — was counted with elementId === 'elements' and its
  // real event types never reached `eventTypes`.
  for (const key of Object.keys(foldBundleEventBindings(events))) {
    const colonIdx = key.lastIndexOf(':')
    if (colonIdx <= 0) continue
    eventTypes.add(key.slice(colonIdx + 1))
    count += 1
  }
  return count
}

export function summarizeRuntimeContract(input: RuntimeContractInput): RuntimeContractSummary {
  const blocks = isRecord(input.blocks) ? input.blocks : {}
  const topLevelFlows = isRecord(input.flows) ? input.flows : {}
  const eventTypes = new Set<string>()
  const resultNames = new Set<string>()
  const computedNames = new Set<string>()
  const stateScopes = new Set<string>()
  const appDataWriteTargets = new Set<string>()
  const localDbWriteTargets = new Set<string>()
  const externalDatasourceTargets = new Set<string>()
  const webMcpToolContracts = new Set<string>()
  const domainActionContracts = new Set<string>()
  const flowNames = new Set<string>()
  let eventCount = 0
  let flowCount = 0
  let localStateCount = 0

  function addFlows(flowMap: Record<string, unknown>) {
    for (const [name, flow] of Object.entries(flowMap)) {
      if (!isRecord(flow)) continue
      const normalizedName = name.toLowerCase()
      if (flowNames.has(normalizedName)) continue
      flowNames.add(normalizedName)
      flowCount += 1
      walkSteps(
        flow.steps,
        resultNames,
        stateScopes,
        appDataWriteTargets,
        localDbWriteTargets,
        externalDatasourceTargets,
        domainActionContracts,
      )
    }
  }

  addFlows(topLevelFlows)
  collectDatasourceContracts(input.data, webMcpToolContracts)
  const manifest = isRecord(input.manifest) ? input.manifest : {}
  collectDatasourceContracts(manifest.datasources, webMcpToolContracts)

  for (const block of Object.values(blocks)) {
    if (!isRecord(block)) continue

    eventCount += collectEventTypes(block.events, eventTypes)

    const state = isRecord(block.state) ? block.state : {}
    if (isRecord(state.localState)) {
      localStateCount += Object.keys(state.localState).length
      if (Object.keys(state.localState).length > 0) stateScopes.add('local')
    }
    if (isRecord(state.computed)) {
      for (const name of Object.keys(state.computed)) computedNames.add(name)
    }

    const flowRoot = isRecord(block.flow) ? block.flow : {}
    const flows = isRecord(flowRoot.flows) ? flowRoot.flows : flowRoot
    addFlows(flows)

    collectDatasourceContracts(block.data, webMcpToolContracts)
    collectDatasourceContracts(block.datasource, webMcpToolContracts)
  }

  const capabilityNotes = new Set<string>()
  if (eventTypes.has('tap')) capabilityNotes.add('tap triggers ActionFlow')
  if (eventTypes.has('change')) capabilityNotes.add('input change dispatch')
  if (computedNames.size > 0) capabilityNotes.add('computed text projection')
  if (appDataWriteTargets.size > 0) capabilityNotes.add('appData global write')
  if (localDbWriteTargets.size > 0) capabilityNotes.add('local DB datasource write')
  if (externalDatasourceTargets.size > 0) capabilityNotes.add('external datasource fetch')
  if (webMcpToolContracts.size > 0) capabilityNotes.add('WebMCP tool registry')
  if (domainActionContracts.size > 0) capabilityNotes.add('domain-operation action contract')
  if (resultNames.size > 0) capabilityNotes.add('_results trace')

  return {
    eventCount,
    eventTypes: sorted(eventTypes),
    flowCount,
    localStateCount,
    appDataWriteTargets: sorted(appDataWriteTargets),
    localDbWriteTargets: sorted(localDbWriteTargets),
    externalDatasourceTargets: sorted(externalDatasourceTargets),
    resultNames: sorted(resultNames),
    computedNames: sorted(computedNames),
    stateScopes: sorted(stateScopes),
    webMcpToolContracts: sorted(webMcpToolContracts),
    domainActionContracts: sorted(domainActionContracts),
    capabilityNotes: sorted(capabilityNotes),
  }
}

export function formatRuntimeContractHeadline(summary: RuntimeContractSummary): string {
  const scopes = summary.stateScopes.length > 0 ? summary.stateScopes.join(' + ') : 'no state writes'
  return `${summary.eventCount} events, ${summary.flowCount} flows, ${scopes}`
}
